import React, { useRef, useCallback, useState } from "react";
import { Group, Rect, Text } from "react-konva";
import Konva from "konva";
import { IMondayTaskController, MondayItemColumn } from "../../controller";
import { KonvaDateIcon } from "./konva-date-icon";
import { format } from "date-fns";
import { FONT_SIZE, COLUMN_HEIGHT, COLUMN_PADDING, COLUMN_WIDTH } from "../../consts";
import { MondayDatePicker } from "./pickers/date-picker";
import { useMondayTaskInteraction } from "../../hooks/use-monday-task-interaction";
import { useAtomValue } from "jotai";
import { stageRefAtom } from "state-atoms/general-atoms";
import tracking from "frontend/tracking";

const ICON_SIZE = 14;

interface DateColumnProps {
  column: MondayItemColumn;
  x: number;
  controller: IMondayTaskController;
}

export const DateColumn = React.memo(({ column, x, controller }: DateColumnProps) => {
  const stageRef = useAtomValue(stageRefAtom);
  const stage = stageRef?.current || null;
  const groupRef = useRef<Konva.Group>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const text = getDateDisplayValue(column);
  const selectedDate = getDateValue(column);

  const handleClick = useCallback(() => {
    setPickerOpen((previous) => !previous);
  }, []);

  const handleDateSelect = useCallback(
    async (date: Date) => {
      tracking.trackAnalyticsEvent("monday_task_column_updated", {
        columnType: "date",
      });
      // Format date as YYYY-MM-DD for Monday API
      const formattedDate = format(date, "yyyy-MM-dd");
      await controller.updateColumnValue(column.id, formattedDate, { id: column.id, type: "date" });
      setPickerOpen(false);
    },
    [column.id, controller]
  );

  const { onMouseEnter, onMouseLeave, onMouseDown, onMouseUp } = useMondayTaskInteraction({
    elementRef: groupRef,
    onClick: handleClick,
  });

  return (
    <>
      <Group
        x={x}
        ref={groupRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      >
        <Rect width={COLUMN_WIDTH} height={COLUMN_HEIGHT} fill="#F6F7FB" cornerRadius={6} />
        <KonvaDateIcon x={COLUMN_PADDING} y={3} width={ICON_SIZE} height={ICON_SIZE} />
        <Text
          x={COLUMN_PADDING / 2 + ICON_SIZE}
          y={1}
          text={text}
          fontSize={FONT_SIZE}
          fill="#7F869B"
          fontFamily="Poppins"
          letterSpacing={0.124}
          width={COLUMN_WIDTH - 2 * COLUMN_PADDING}
          height={COLUMN_HEIGHT}
          align="center"
          verticalAlign="middle"
          ellipsis={true}
          wrap="none"
        />

        <MondayDatePicker
          konvaNode={groupRef.current}
          stage={stage}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          selectedDate={selectedDate}
          onSelect={handleDateSelect}
          buttonRef={groupRef}
        />
      </Group>
    </>
  );
});

DateColumn.displayName = "DateColumn";

function getDateDisplayValue(column: MondayItemColumn): string {
  const dateValue = column.value as string;
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  const formattedDate = format(date, "dd MMM '‘'yy");
  return formattedDate;
}

function getDateValue(column: MondayItemColumn): Date | undefined {
  const dateValue = column.value as string;
  if (!dateValue) return undefined;
  return new Date(dateValue);
}
